/*
 * WHAT A NAME IS, BEFORE YOU OPEN IT.
 *
 * The card that follows the pointer across the field — the figure's name, and what it is. Theirs has
 * had it from the start and ours had nothing at all: the map never once told a reader that the light
 * under their pointer had a name, let alone which. It is the step between looking at the field and
 * opening a name, and it was missing.
 *
 * Placed by the caller in screen pixels, above the figure and clear of it, and flipped below when it
 * would otherwise run off the top. Inert to the pointer, so it can never eat the tap it is inviting.
 *
 * `aria-hidden`: a card that appears under a mouse is not reachable by a keyboard, and announcing it
 * would interrupt a screen reader with something they cannot act on. The names are reachable through
 * the search, which is a real control with real results.
 */
export interface MapHoverCardProps {
  name: string;
  sub: string;
  /** Where the figure is, in screen pixels. */
  x: number;
  y: number;
}

/**
 * THE GAP BETWEEN THE CARD AND THE FIGURE, and how much room it needs to sit above at all.
 *
 * It used to be a `top` alone: the card’s TOP went 46px above the figure and the box hung DOWN
 * from there, so its lower edge landed on the figure’s centre — across the row where the map
 * writes the figure’s own name. Caught in a screenshot at the middle range, where the card read
 * "Abraham · Person · 5 corpora" with the word "…raham" sticking out from under it.
 *
 * The card is now anchored by its BOTTOM edge (`translate(-50%, -100%)` in the stylesheet), so
 * this is a true gap and the card can never share a row with the label. Theirs sits clear of its
 * figure the same way.
 */
const GAP = 18;
const NEEDS = 96;

export function MapHoverCard({ name, sub, x, y }: MapHoverCardProps): React.JSX.Element {
  const below = y < NEEDS;
  return (
    <div
      className="map-hover"
      style={{
        left: `${x}px`,
        top: `${below ? y + GAP : y - GAP}px`,
        /* WHICH EDGE THE CARD HANGS FROM, here rather than in the stylesheet — because it is the
           whole of the fix and jsdom cannot see a stylesheet. Kept inline so the test beside this
           can assert it; the old test was named for this property and checked only that `top` was
           a smaller number, which was true while the box hung down over the figure. */
        transform: below ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
      }}
      aria-hidden="true"
      data-testid="map-hover"
    >
      <span className="map-hover-name">{name}</span>
      {/*
        * A SPACE, BECAUSE THE TWO SPANS ABUT IN `textContent`. Read back, the card said
        * "AdamPerson · 5 corpora" where theirs says "Adam Person · 4 corpora" — the name and what
        * it is running into one word. It changes nothing on screen (they are separate lines) and
        * everything to anything reading the card: the same merge cost a run of readings of 880 and
        * 8,865 labels when the readout grew a second line.
        */}
      {' '}
      <span className="map-hover-sub">{sub}</span>
    </div>
  );
}
